import type { ReactNode } from 'react';
import { IonIcon, useIonRouter } from '@ionic/react';
import { briefcaseOutline } from 'ionicons/icons';
import type { CareerRoleEnum } from '../../../../types/graphql-generated';
import { CAREER_ROLE_LABELS } from '../careerRoles';

export interface CareerEntryItemEntry {
  id: string;
  role: CareerRoleEnum;
  startDate: string;
  endDate?: string | null;
  details?: string | null;
  careerOrganization: { id: string; name: string };
}

interface CareerEntryItemProps {
  entry: CareerEntryItemEntry;
  /** Extra controls shown on the right (e.g. edit / delete). */
  actions?: ReactNode;
  /** Route prefix for overlap pages in the current tab's navigation stack. */
  overlapsPathPrefix?: string;
}

const formatCareerDate = (date: string) =>
  new Intl.DateTimeFormat('es-ES', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));

// One row of a career history. Tapping the organization name opens the list
// of other people that were in that organization during the entry's period.
export const CareerEntryItem = ({
  entry,
  actions,
  overlapsPathPrefix = '/app/main/perfil/experiencia',
}: CareerEntryItemProps) => {
  const ionRouter = useIonRouter();

  return (
    <div className="flex gap-3 border-b border-[#e8ebf1] p-4 last:border-b-0">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eef3fa] text-[#0a2a5b]">
        <IonIcon icon={briefcaseOutline} />
      </div>
      <div className="min-w-0 flex-1">
        <button
          type="button"
          className="block max-w-full truncate bg-transparent p-0 text-left font-heading text-[15px] font-extrabold text-[#0a2a5b] underline-offset-2 hover:underline"
          onClick={() => ionRouter.push(`${overlapsPathPrefix}/${entry.id}`)}
        >
          {entry.careerOrganization.name}
        </button>
        <p className="m-0 mt-0.5 text-sm font-semibold text-slate-600">
          {CAREER_ROLE_LABELS[entry.role]}
        </p>
        {entry.details && (
          <p className="m-0 mt-0.5 break-words text-sm text-slate-600">{entry.details}</p>
        )}
        <p className="m-0 mt-1 text-xs text-slate-500">
          {formatCareerDate(entry.startDate)} –{' '}
          {entry.endDate ? formatCareerDate(entry.endDate) : 'Actualidad'}
        </p>
      </div>
      {actions && <div className="flex shrink-0 items-start">{actions}</div>}
    </div>
  );
};
